/**
 * toolVisuals
 * ---------------------------------------------------------------------------
 * Shared accent + glyph vocabulary for the public tool pages and the visitor
 * home grid, so both surfaces look like the same product. Every class is built
 * from existing semantic tokens — no hardcoded colours.
 */
import type React from 'react';
import type { ToolLanding, ToolSlug } from '@/config/toolLandings';
import {
  CareerGlyph,
  RelationshipsGlyph,
  AstrologyGlyph,
  PeopleGlyph,
  LikerGlyph,
} from '@/components/nav/icons';
import type { SkillGlyph } from '@/components/skilltree/svg/glyph';

export type ToolAccent = ToolLanding['accent'];

export interface ToolAccentClasses {
  /** Large soft wash behind the hero / CTA band. */
  wash: string;
  /** Small square icon tile. */
  tile: string;
  /** Border/ring tint. */
  ring: string;
  /** Hover border tint (static class so Tailwind keeps it). */
  hoverRing: string;
  /** Text colour. */
  text: string;
  /** Thin bar / divider tint. */
  bar: string;
  /** Glow used behind the sample panel. */
  glow: string;
}

const ACCENTS: Record<ToolAccent, ToolAccentClasses> = {
  primary: {
    wash: 'from-primary/25 via-primary/5 to-transparent',
    tile: 'bg-primary/15 text-primary',
    ring: 'border-primary/30',
    hoverRing: 'hover:border-primary/50',
    text: 'text-primary',
    bar: 'bg-primary/40',
    glow: 'bg-primary/20',
  },
  secondary: {
    wash: 'from-secondary/25 via-secondary/5 to-transparent',
    tile: 'bg-secondary/15 text-secondary',
    ring: 'border-secondary/30',
    hoverRing: 'hover:border-secondary/50',
    text: 'text-secondary',
    bar: 'bg-secondary/40',
    glow: 'bg-secondary/20',
  },
  accent: {
    wash: 'from-accent/25 via-accent/5 to-transparent',
    tile: 'bg-accent/15 text-accent',
    ring: 'border-accent/30',
    hoverRing: 'hover:border-accent/50',
    text: 'text-accent',
    bar: 'bg-accent/40',
    glow: 'bg-accent/20',
  },
  success: {
    wash: 'from-success/25 via-success/5 to-transparent',
    tile: 'bg-success/15 text-success',
    ring: 'border-success/30',
    hoverRing: 'hover:border-success/50',
    text: 'text-success',
    bar: 'bg-success/40',
    glow: 'bg-success/20',
  },
  info: {
    wash: 'from-info/25 via-info/5 to-transparent',
    tile: 'bg-info/15 text-info',
    ring: 'border-info/30',
    hoverRing: 'hover:border-info/50',
    text: 'text-info',
    bar: 'bg-info/40',
    glow: 'bg-info/20',
  },
};

export const toolAccent = (accent: ToolAccent): ToolAccentClasses => ACCENTS[accent];

export const TOOL_GLYPHS: Record<ToolSlug, SkillGlyph> = {
  career: CareerGlyph,
  relationships: RelationshipsGlyph,
  astrology: AstrologyGlyph,
  enterprise: PeopleGlyph,
  liker: LikerGlyph,
};

/**
 * A faint repeating background pattern, one shape per tool, so the pages differ
 * by more than accent colour. Currentcolor-free: drawn with a fixed low-opacity
 * white stroke and then blended, which keeps it token-safe in the dark theme.
 */
const PATTERN_PATHS: Record<ToolSlug, string> = {
  // Anvil-ish bracket
  career: "%3Cpath d='M10 26h20l-4 8H14z' fill='none' stroke='%23fff' stroke-width='1'/%3E",
  // Two linked rings
  relationships:
    "%3Ccircle cx='16' cy='20' r='7' fill='none' stroke='%23fff' stroke-width='1'/%3E%3Ccircle cx='26' cy='20' r='7' fill='none' stroke='%23fff' stroke-width='1'/%3E",
  // Star burst
  astrology:
    "%3Cpath d='M20 8v24M8 20h24M12 12l16 16M28 12L12 28' stroke='%23fff' stroke-width='0.8' fill='none'/%3E",
  // Grid of nodes
  enterprise:
    "%3Ccircle cx='12' cy='12' r='2.5' fill='none' stroke='%23fff'/%3E%3Ccircle cx='28' cy='12' r='2.5' fill='none' stroke='%23fff'/%3E%3Ccircle cx='20' cy='28' r='2.5' fill='none' stroke='%23fff'/%3E%3Cpath d='M12 12h16M12 12l8 16M28 12l-8 16' stroke='%23fff' stroke-width='0.6'/%3E",
  // Stacked cards
  liker:
    "%3Crect x='12' y='10' width='16' height='20' rx='3' fill='none' stroke='%23fff' stroke-width='1'/%3E%3Cpath d='M16 34h12' stroke='%23fff' stroke-width='1'/%3E",
};

export function toolPattern(slug: ToolSlug): React.CSSProperties {
  return {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E${PATTERN_PATHS[slug]}%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
  };
}

